/**
 * الذكاء الاصطناعي — the Super Admin's control over the AI.
 *
 * Three jobs on one screen, in the order an admin needs them:
 *
 *   1. **Is the engine alive?** The ladder's depth, straight from
 *      /api/ai/health. A key count of zero here explains every "the AI does
 *      not work" report in one line, before anyone opens a log.
 *
 *   2. **Who may use it, and who may use the paid tier?** The grant table.
 *      This is the RBAC gate the product asked for: the premium toggle only
 *      appears in a doctor's console for doctors switched on here.
 *
 *   3. **What has it done, and what did it cost?** Thirty days of runs,
 *      per model, plus the failures with their full fallback descent.
 *
 * The three are on one screen rather than three because they are read
 * together: a spike in failures is explained by the ladder, and a spike in
 * spend is explained by the grants.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  PageHeader,
  Panel,
  Spinner,
  StatCard,
} from '../../components/common';
import { useAsync } from '../../hooks/useAsync';
import { useDebounced } from '../../hooks/useLocalStorage';
import { useNotifications } from '../../context/NotificationContext';
import { adminApi, aiApi } from '../../api/endpoints';
import { invalidateAiCapabilities } from '../ai/useAiAnalysis';

const shortModel = (model) => {
  if (!model) return '—';
  const withoutVendor = model.includes('/') ? model.split('/')[1] : model;
  return withoutVendor.replace(':free', '');
};

export function AdminAiAccess() {
  const { success, error: toastError } = useNotifications();

  const grants = useAsync(() => adminApi.aiAccess(), [], { initialData: [] });
  const health = useAsync(() => aiApi.health(), [], { initialData: null });
  const stats = useAsync(() => adminApi.aiStats(), [], { initialData: null });
  const failures = useAsync(() => adminApi.aiLogs({ failedOnly: 'true', limit: 20 }), [], {
    initialData: [],
  });

  const [query, setQuery] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const debounced = useDebounced(query, 250);

  // Memoised rather than `grants.data || []` inline: the fallback allocates a
  // fresh array on every render, which would invalidate both useMemos below
  // and re-filter the whole table on every keystroke.
  const doctors = useMemo(() => grants.data || [], [grants.data]);

  const filtered = useMemo(() => {
    const needle = debounced.trim().toLowerCase();
    if (!needle) return doctors;
    return doctors.filter(
      (doctor) =>
        String(doctor.name).toLowerCase().includes(needle) ||
        String(doctor.username).toLowerCase().includes(needle),
    );
  }, [doctors, debounced]);

  const counts = useMemo(
    () => ({
      total: doctors.length,
      premium: doctors.filter((d) => d.premiumEnabled).length,
      disabled: doctors.filter((d) => !d.aiEnabled).length,
      runsToday: doctors.reduce(
        (sum, d) => sum + (d.usageToday?.standard || 0) + (d.usageToday?.premium || 0),
        0,
      ),
    }),
    [doctors],
  );

  /**
   * Persist one field, optimistically.
   *
   * The row updates before the request lands and rolls back if it fails. A
   * switch that waits 300ms to move reads as broken, and an admin toggling
   * twenty doctors would feel every one of them.
   */
  const patch = async (doctor, changes, message) => {
    const previous = doctors;
    setSavingId(doctor.id);

    grants.setData(
      doctors.map((row) => (row.id === doctor.id ? { ...row, ...changes } : row)),
    );

    try {
      await adminApi.setAiAccess(doctor.id, changes);
      // An admin who granted themselves premium would otherwise wait out the
      // capability cache's TTL before their own console offered the toggle.
      invalidateAiCapabilities();
      success(message);
    } catch (err) {
      grants.setData(previous);
      toastError(err.message || 'تعذر حفظ الصلاحية');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="الذكاء الاصطناعي"
        subtitle="صلاحيات التحليل الذكي، حالة المحرك، والتكلفة"
        icon="sparkles"
        actions={
          <Button
            variant="ghost"
            icon="refresh-cw"
            loading={grants.loading}
            onClick={() => {
              grants.reload();
              health.reload();
              stats.reload();
              failures.reload();
            }}
          >
            تحديث
          </Button>
        }
      />

      <EngineHealth health={health} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="users" label="أطباء" value={counts.total} accent="cyan" />
        <StatCard icon="crown" label="مُفعّل لهم المتقدم" value={counts.premium} accent="amber" />
        <StatCard icon="ban" label="موقوف عنهم الذكاء" value={counts.disabled} accent="red" />
        <StatCard icon="activity" label="تحليلات اليوم" value={counts.runsToday} accent="emerald" />
      </div>

      {/* ── The grant table ────────────────────────────────────────────── */}
      <Panel>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-lg font-bold text-white">
            <Icon name="shield-check" size={20} className="text-amber-400" />
            صلاحيات الأطباء
          </h3>
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ابحث باسم الطبيب..."
              className="pr-9"
            />
          </div>
        </div>

        {/* The distinction that costs money, said once, where the switch is —
            rather than in documentation nobody opens. */}
        <p className="mb-4 flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/[0.07] p-3 text-xs leading-relaxed text-amber-200/90">
          <Icon name="info" size={14} className="mt-0.5 shrink-0" />
          <span>
            <strong>النموذج القياسي</strong> مجاني ومتاح افتراضياً لكل طبيب.{' '}
            <strong>النموذج المتقدم</strong> مدفوع بالاستدعاء — لا يظهر زر تفعيله في شاشة الطبيب
            إلا بعد تفعيله هنا، والحد اليومي هو سقف التكلفة لكل طبيب.
          </span>
        </p>

        {grants.error ? (
          <ErrorState error={grants.error} onRetry={grants.reload} />
        ) : grants.loading && doctors.length === 0 ? (
          <Spinner label="جاري تحميل الصلاحيات..." />
        ) : filtered.length === 0 ? (
          <EmptyState icon="user-x" title="لا يوجد أطباء مطابقون" />
        ) : (
          <div className="custom-scrollbar -mx-2 overflow-x-auto px-2">
            <table className="w-full min-w-[820px] text-right">
              <thead className="border-b border-white/10 text-xs text-slate-400">
                <tr>
                  <th className="w-1/3 pb-3 font-medium">الطبيب</th>
                  <th className="w-[110px] pb-3 text-center font-medium">التحليل الذكي</th>
                  <th className="w-[150px] pb-3 text-center font-medium text-amber-400">
                    النموذج المتقدم
                  </th>
                  <th className="w-[130px] pb-3 text-center font-medium">الحد اليومي</th>
                  <th className="pb-3 text-center font-medium">استخدام اليوم</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((doctor) => (
                  <GrantRow
                    key={doctor.id}
                    doctor={doctor}
                    saving={savingId === doctor.id}
                    onPatch={patch}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <UsageStats stats={stats} />
      <FailureLog failures={failures} expanded={expanded} setExpanded={setExpanded} />
    </div>
  );
}

/* ==========================================================================
   Engine health
   ========================================================================== */

/**
 * The one panel that answers "why is nothing working".
 *
 * Ladder depth is `keys × models`, and it is printed rather than derived in
 * the reader's head because it is the number that matters: a platform with one
 * key and eight models is one revoked key away from an outage, and that fact
 * should be legible without arithmetic.
 */
function EngineHealth({ health }) {
  if (health.loading) return <Spinner variant="inline" label="جاري فحص محرك الذكاء..." />;

  if (health.error || !health.data) {
    return (
      <Panel className="border-red-500/25">
        <div className="flex items-start gap-3">
          <Icon name="alert-triangle" size={20} className="mt-0.5 shrink-0 text-red-400" />
          <div>
            <h4 className="font-bold text-red-300">تعذر الوصول لمحرك التحليل</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-400">
              نقطة الفحص <code className="text-slate-300">/api/ai/health</code> لا تستجيب. غالباً
              الخادم يعمل بنسخة سابقة لا تحتوي على مسارات الذكاء الاصطناعي — شغّل{' '}
              <code className="text-slate-300">9-server-ai-upgrade.sh</code> على الخادم.
            </p>
          </div>
        </div>
      </Panel>
    );
  }

  const data = health.data;
  const ok = data.configured;

  return (
    <Panel className={ok ? 'border-emerald-500/20' : 'border-amber-500/30'}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-11 w-11 items-center justify-center rounded-xl ${
              ok
                ? 'border border-emerald-500/25 bg-emerald-500/15 text-emerald-300'
                : 'border border-amber-500/25 bg-amber-500/15 text-amber-300'
            }`}
          >
            <Icon name={ok ? 'zap' : 'alert-triangle'} size={20} />
          </span>
          <div>
            <div className="text-sm font-black text-white">
              {ok ? 'المحرك يعمل' : 'المحرك غير مُهيّأ'}
            </div>
            <div className="text-[11px] text-slate-500">
              {ok
                ? `${data.keyCount} مفتاح × ${data.fallbackModels?.length || 0} نموذج`
                : 'AI_API_KEYS غير مضبوط في backend/.env'}
            </div>
          </div>
        </div>

        <HealthFact label="عمق السلسلة" value={data.ladderDepth} hint="محاولة قبل ظهور أي خطأ" />
        <HealthFact label="النموذج الأساسي" value={shortModel(data.model)} ltr />
        <HealthFact
          label="النموذج المتقدم"
          value={shortModel(data.premiumModels?.[0])}
          ltr
          tone="amber"
        />
        <HealthFact
          label="مهلة أول رد"
          value={`${(data.timeouts?.connectMs || 0) / 1000} ث`}
          hint="قبل الانتقال للنموذج التالي"
        />
      </div>

      {!ok && data.hint && (
        <p className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/[0.07] p-2.5 text-[11px] text-amber-200">
          {data.hint}
        </p>
      )}
    </Panel>
  );
}

function HealthFact({ label, value, hint, ltr = false, tone }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div
        className={`text-sm font-bold ${tone === 'amber' ? 'text-amber-300' : 'text-white'}`}
        dir={ltr ? 'ltr' : undefined}
        style={ltr ? { textAlign: 'right' } : undefined}
      >
        {value ?? '—'}
      </div>
      {hint && <div className="text-[10px] text-slate-600">{hint}</div>}
    </div>
  );
}

/* ==========================================================================
   One doctor's row
   ========================================================================== */

function GrantRow({ doctor, saving, onPatch }) {
  const usage = doctor.usageToday || { standard: 0, premium: 0 };

  return (
    <tr className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
      <td className="py-3">
        <div className="font-bold text-white">{doctor.name}</div>
        <div className="text-[11px] text-slate-500" dir="ltr">
          @{doctor.username}
          {doctor.status !== 'active' && (
            <span className="text-amber-400"> · {doctor.status}</span>
          )}
        </div>
      </td>

      <td className="py-3 text-center">
        <SwitchCell
          on={doctor.aiEnabled}
          disabled={saving}
          onChange={() =>
            onPatch(
              doctor,
              { aiEnabled: !doctor.aiEnabled },
              doctor.aiEnabled ? 'تم إيقاف التحليل الذكي' : 'تم تفعيل التحليل الذكي',
            )
          }
        />
      </td>

      <td className="py-3 text-center">
        <SwitchCell
          on={doctor.premiumEnabled}
          premium
          // Premium without base access is a grant that can never fire — the
          // server checks `canUseAi` first. Disabling it here says so before an
          // admin sets it and wonders why nothing changed.
          disabled={saving || !doctor.aiEnabled}
          onChange={() =>
            onPatch(
              doctor,
              { premiumEnabled: !doctor.premiumEnabled },
              doctor.premiumEnabled
                ? 'تم سحب صلاحية النموذج المتقدم'
                : 'تم منح صلاحية النموذج المتقدم',
            )
          }
        />
      </td>

      <td className="py-3 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <input
            type="number"
            min="0"
            defaultValue={doctor.premiumDailyLimit}
            disabled={saving || !doctor.premiumEnabled}
            title="الحد اليومي للنموذج المتقدم — صفر يعني بلا حد"
            // onBlur, not onChange: saving on every keystroke would fire a
            // request per digit of "20".
            onBlur={(event) => {
              const next = Math.max(0, Number(event.target.value) || 0);
              if (next === doctor.premiumDailyLimit) return;
              onPatch(doctor, { premiumDailyLimit: next }, 'تم تحديث الحد اليومي');
            }}
            className="w-16 rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-center text-xs text-white outline-none focus:border-amber-500 disabled:opacity-40"
          />
          <span className="text-[10px] text-slate-600">/يوم</span>
        </div>
      </td>

      <td className="py-3 text-center">
        <div className="flex items-center justify-center gap-2 text-[11px]">
          <Badge className="border-cyan-500/25 bg-cyan-500/10 text-cyan-300">
            {usage.standard} قياسي
          </Badge>
          {usage.premium > 0 && (
            <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
              {usage.premium} متقدم
            </Badge>
          )}
        </div>
      </td>
    </tr>
  );
}

function SwitchCell({ on, premium = false, disabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onChange}
      className="ai-toggle mx-auto"
      data-on={String(on)}
      // The stylesheet's "on" state is the gold/violet premium gradient. The
      // base-access switch overrides it to cyan so the two columns cannot be
      // confused at a glance — one of them costs money and the other does not.
      style={
        on && !premium
          ? {
              background: 'linear-gradient(135deg,#06b6d4,#3b82f6)',
              borderColor: 'rgba(34,211,238,.5)',
              boxShadow: '0 0 14px rgba(6,182,212,.45)',
            }
          : undefined
      }
    >
      <span className="ai-toggle__knob" />
    </button>
  );
}

/* ==========================================================================
   Usage
   ========================================================================== */

function UsageStats({ stats }) {
  if (stats.loading) return <Spinner variant="inline" label="جاري تحميل الإحصائيات..." />;
  if (stats.error || !stats.data) return null;

  const { totals, byModel } = stats.data;
  const successRate = totals.total > 0 ? Math.round((totals.succeeded / totals.total) * 100) : null;

  return (
    <Panel>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-white">
        <Icon name="trending-up" size={20} className="text-emerald-400" />
        آخر 30 يوماً
      </h3>

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="activity" label="إجمالي التحليلات" value={totals.total} accent="cyan" />
        <StatCard
          icon="check-circle-2"
          label="نسبة النجاح"
          value={successRate === null ? '—' : `${successRate}%`}
          accent={successRate === null || successRate >= 90 ? 'emerald' : 'amber'}
        />
        <StatCard icon="crown" label="استدعاءات مدفوعة" value={totals.premium} accent="amber" />
        <StatCard icon="alert-triangle" label="فشل" value={totals.failed} accent="red" />
      </div>

      {byModel.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-500">لا توجد تحليلات بعد.</p>
      ) : (
        <div className="custom-scrollbar -mx-2 overflow-x-auto px-2">
          <table className="w-full min-w-[560px] text-right">
            <thead className="border-b border-white/10 text-xs text-slate-400">
              <tr>
                <th className="pb-3 font-medium">النموذج الذي ردّ</th>
                <th className="w-24 pb-3 text-center font-medium">مرات</th>
                <th className="w-32 pb-3 text-center font-medium">متوسط أول رد</th>
                <th className="w-40 pb-3 text-center font-medium">متوسط المحاولات</th>
              </tr>
            </thead>
            <tbody>
              {byModel.map((row) => (
                <tr key={row.model} className="border-b border-white/5">
                  <td className="py-2.5 text-xs text-slate-300" dir="ltr" style={{ textAlign: 'right' }}>
                    {shortModel(row.model)}
                  </td>
                  <td className="py-2.5 text-center text-sm font-bold text-white">{row.runs}</td>
                  <td className="py-2.5 text-center text-xs text-slate-400">
                    {row.avgFirstTokenMs != null ? `${row.avgFirstTokenMs} ms` : '—'}
                  </td>
                  <td className="py-2.5 text-center">
                    {/* Average attempts is the early-warning number. 1.0 means
                        the first model always answers; a drift upward is a key
                        or a model dying, visible here days before it takes the
                        feature down. */}
                    <span
                      className={`text-sm font-bold ${
                        (row.avgAttempts || 1) > 2.5 ? 'text-amber-300' : 'text-slate-300'
                      }`}
                    >
                      {row.avgAttempts ?? '—'}
                    </span>
                    {(row.avgAttempts || 1) > 2.5 && (
                      <div className="text-[10px] text-amber-400/70">السلسلة تُستهلك بعمق</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* ==========================================================================
   Failures
   ========================================================================== */

/**
 * Recent failures, each expandable to its full fallback descent.
 *
 * The descent is the diagnosis: it names every masked key and model tried, the
 * HTTP status and the upstream message. That is the difference between "the AI
 * failed" and "key #3 is out of credit and gemma-4-31b was retired".
 */
function FailureLog({ failures, expanded, setExpanded }) {
  const rows = failures.data || [];

  if (failures.loading) return <Spinner variant="inline" label="جاري تحميل سجل الأخطاء..." />;

  return (
    <Panel>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-bold text-white">
          <Icon name="alert-triangle" size={20} className="text-red-400" />
          سجل التشخيص
        </h3>
        <Button variant="ghost" icon="refresh-cw" onClick={failures.reload}>
          تحديث
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="py-6 text-center">
          <Icon name="check-circle-2" size={32} className="mx-auto mb-2 text-emerald-400/60" />
          <p className="text-sm text-slate-400">لا توجد أخطاء مسجّلة. المحرك يعمل بلا مشاكل.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className="overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.02]"
            >
              <button
                type="button"
                onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 p-3 text-right transition-colors hover:bg-white/[0.03]"
              >
                <Badge
                  className={
                    row.outcome === 'denied'
                      ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                      : 'border-red-500/30 bg-red-500/10 text-red-300'
                  }
                >
                  {row.outcome}
                </Badge>
                <span className="text-xs text-slate-300">{row.feature}</span>
                <span className="text-[11px] text-slate-500">
                  {new Date(row.createdAt).toLocaleString('ar-EG')}
                </span>
                <span className="text-[11px] text-slate-600">
                  {row.attemptCount} محاولة
                </span>
                <span className="flex-1" />
                <Icon
                  name={expanded === row.id ? 'chevron-up' : 'chevron-down'}
                  size={14}
                  className="text-slate-500"
                />
              </button>

              {expanded === row.id && (
                <div className="border-t border-white/[0.07] bg-black/25 p-3">
                  <div className="mb-2 grid gap-1 text-[11px] text-slate-400 sm:grid-cols-2">
                    <span>المستخدم: {row.userId ?? '—'} ({row.userRole || '—'})</span>
                    <span>المريض: {row.patientId ?? row.patientCode ?? '—'}</span>
                    <span>الطبقة: {row.tier}</span>
                    <span>حجم الطلب: {row.promptChars} حرف</span>
                  </div>

                  {(row.attempts || []).length > 0 ? (
                    <ol className="space-y-1">
                      {row.attempts.map((attempt, index) => (
                        <li
                          key={index}
                          className="rounded-lg bg-black/40 p-2 font-mono text-[10.5px] leading-relaxed text-slate-400"
                          dir="ltr"
                        >
                          <span className="text-slate-500">#{index + 1}</span>{' '}
                          <span className="text-cyan-300">{attempt.model}</span>{' '}
                          <span className="text-slate-600">via {attempt.key}</span>{' '}
                          <span className="text-amber-300">
                            {attempt.outcome}
                            {attempt.status ? ` (${attempt.status})` : ''}
                          </span>{' '}
                          <span className="text-slate-600">{attempt.ms}ms</span>
                          {attempt.error && (
                            <div className="mt-0.5 text-red-300/70">{attempt.error}</div>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-[11px] text-slate-500">
                      {row.error || 'لم تُسجَّل أي محاولة — الطلب رُفض قبل الوصول للمحرك.'}
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default AdminAiAccess;
